import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import NativeModal from 'react-modal';
import classNames from 'classnames';

import { VIDEO_LIST } from '../../constants/customerOnboarding';

import { track } from '@xanadu/ui-shared/interfaces/woopra';

import Button from '@xanadu/ui-shared/components/widgets/Button';
import CustomerOnboardingCards from '../CustomerOnboardingCards';

import IconClose from '../../../images/icons/misc/close.svg';
import IconCollapse from '../../../images/icons/misc/icon-videocollapse.svg';
import IconPlayButton from '../../../images/icons/misc/icon-playbutton.svg';

import styles from './CustomerOnboarding.module.scss';

NativeModal.setAppElement(document.getElementById('root'));

const CustomerOnboarding = ({
  widthSize,
  isLoggedIn,
  startTourMode,
  firstTimeVisitor,
  setHighlightedSidebarButton,
  registrationRequest,
  toggleCustomerOnboardingPopup,
}) => {
  const videoListScrollIntervalRef = useRef();
  const [videoListNode, setVideoListNode] = useState(null);
  const [videoListNodeContent, setVideoListNodeContent] = useState(null);
  const [prepareForMobileTour, setPrepareForMobileTour] = useState(false);
  const [showMobileTour, setShowMobileTour] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');

  const isMobile = useMemo(() => widthSize < 1200, [widthSize]);

  const onVideoSelect = (url) => () => {
    track('Marketing', 'Onboarding video selected');
    setSelectedVideoUrl(url);
  };

  const handleClose = ({ showTourReminder = true } = {}) => {
    toggleCustomerOnboardingPopup({
      visible: false,
      firstTimeVisitor: false
    });

    if (showTourReminder && firstTimeVisitor) {
      setHighlightedSidebarButton('tour');

      setTimeout(() => {
        setHighlightedSidebarButton('');
      }, 6000);
    }
  };

  const onCloseButtonClick = () => {
    if (selectedVideoUrl && !isMobile) {
      setSelectedVideoUrl('');
      return;
    }

    handleClose();
    track('Marketing', 'Onboarding closed');
  };

  const handleTour = () => {
    if (firstTimeVisitor) {
      track('Marketing', 'Tour Start from Popup');
    } else {
      track('Marketing', 'Tour Start from RHN');
    }

    if (isMobile) {
      setPrepareForMobileTour(true);

      setTimeout(() => {
        setPrepareForMobileTour(false);
        setShowMobileTour(true);
      }, 270);

      return;
    }

    handleClose({ showTourReminder: false });

    setTimeout(() => {
      startTourMode();
    }, 100);
  };

  const handleSignUp = () => {
    handleClose({ showTourReminder: false });

    setTimeout(() => {
      registrationRequest({ show: true, promoRegistrationImage: null, checked: false });
      track('Marketing', 'Reg opened from Tour');
    }, 100);
  };

  const videoListRef = useCallback((node) => {
    if (node !== null) {
      setVideoListNode(node);
    }
  }, []);

  const videoListRefContent = useCallback((node) => {
    if (node !== null) {
      setVideoListNodeContent(node);
    }
  }, []);

  const handleVideoListScroll = (event) => {
    clearInterval(videoListScrollIntervalRef.current);

    videoListScrollIntervalRef.current = setInterval(() => {
      const { width: contentWidth } = videoListNodeContent.getBoundingClientRect();
      const { width: containerWidth } = videoListNode.getBoundingClientRect();

      const containerScrollCenter = ((contentWidth - containerWidth) / 2);

      // values are based on the VIDEO div CSS style
      // if you plan to update the styles, make sure those values are re-calculated and updated;
      // no need to write logic for it as it should not change often
      if (event.target.scrollLeft <= 155) {
        videoListNode.scroll({
          left: 0,
          behavior: 'smooth',
        });
      } else if (event.target.scrollLeft <= 466) {
        videoListNode.scroll({
          left: containerScrollCenter,
          behavior: 'smooth',
        });
      } else {
        videoListNode.scroll({
          left: contentWidth - containerWidth,
          behavior: 'smooth',
        });
      }

      clearInterval(videoListScrollIntervalRef.current);
    }, 120);
  };

  useEffect(() => {
    if (videoListNode) {
      videoListNode.addEventListener('scroll', handleVideoListScroll);
    }

    return () => {
      if (videoListNode) {
        videoListNode.removeEventListener('scroll', handleVideoListScroll);
      }
    };
  }, [videoListNode]);

  useEffect(() => {
    if (isMobile && videoListNode) {
      videoListNode.scroll({
        left: 308,
        behavior: 'smooth',
      });
    }
  }, [isMobile, videoListNode]);

  useEffect(() => {
    if (isMobile) {
      document.documentElement.style.overflow = 'hidden';
    }

    return () => {
      if (isMobile) {
        document.documentElement.style.overflow = '';
      }
    };
  }, [isMobile]);

  useEffect(() => {
    track('Marketing', 'Onboarding opened');

    return () => {
      try {
        localStorage.setItem('mb-onboarding-visited', true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Error accessing localStorage', e.message);
      }
    };
  }, []);

  const modalClass = classNames({
    'react-modal': true,
    'react-modal--center': true,
    [styles.modal]: true,
  });

  const bodyOpenClass = classNames({
    'ReactModal__Body--open': true,
    'ReactModal__Body--open--withScroll': true,
  });

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--mobile']]: isMobile,
    [styles['main--videoSelected']]: Boolean(selectedVideoUrl),
  });

  return (
    <NativeModal
      isOpen
      className={modalClass}
      bodyOpenClassName={bodyOpenClass}
      overlayClassName="mb-modal__overlay"
      shouldCloseOnOverlayClick
    >
      {prepareForMobileTour ? null : (
        <Fragment>
          {showMobileTour ? (
            <CustomerOnboardingCards
              isLoggedIn={isLoggedIn}
              registrationRequest={registrationRequest}
              toggleCustomerOnboardingPopup={toggleCustomerOnboardingPopup}
            />
          ) : (
            <Fragment>
              <div className={mainClass}>
                <button
                  className={styles.close}
                  onClick={onCloseButtonClick}
                >
                  {selectedVideoUrl && !isMobile ? (
                    <IconCollapse />
                  ) : (
                    <IconClose />
                  )}
                </button>
                {selectedVideoUrl && !isMobile ? (
                  <iframe
                    width="100%"
                    height="100%"
                    className={styles.videoIframe}
                    src={`${selectedVideoUrl}?autoplay=1`}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                ) : (
                  <Fragment>
                    <div className={styles.content}>
                      <h2 className={styles.title}>
                        {firstTimeVisitor ? (
                          <span>Welcome to Matchbook</span>
                        ) : (
                          <span>Explore Matchbook</span>
                        )}
                        <span> - </span>
                        <span>A Better Way to Bet</span>
                      </h2>
                      <div className={styles.subTitle}>
                        An introduction to Exchange Betting
                      </div>
                      <p>
                        {"We get it. Betting Exhanges can be a little scary the first time you use one. That's why we have a series of helpful short videos, as well as a guided tour,  to get you up to speed."}
                      </p>
                      <div className={styles.videoList} ref={videoListRef}>
                        <span ref={videoListRefContent}>
                          <div
                            role="button"
                            className={styles.video}
                            onClick={onVideoSelect(VIDEO_LIST[1].url)}
                          >
                            {selectedVideoUrl === VIDEO_LIST[1].url && isMobile ? (
                              <iframe
                                width="100%"
                                height="100%"
                                className={styles.videoIframe}
                                src={`${selectedVideoUrl}?autoplay=1`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                              />
                            ) : (
                              <div className={styles.videoImage}>
                                <span style={{ backgroundImage: `url(${VIDEO_LIST[1].image})` }} />
                                <i className={styles.videoImageIcon}>
                                  <IconPlayButton />
                                </i>
                              </div>
                            )}
                            <div className={styles.videoTitle}>
                              What is Commission?
                            </div>
                            <span className={styles.videoDescription}>
                              {"If you've heard about a betting exchange, you've probably heard about commission. So what is it exactly and how does it fit in on an exchange?"}
                            </span>
                          </div>
                          <div
                            role="button"
                            className={styles.video}
                            onClick={onVideoSelect(VIDEO_LIST[0].url)}
                          >
                            {selectedVideoUrl === VIDEO_LIST[0].url && isMobile ? (
                              <iframe
                                width="100%"
                                height="100%"
                                className={styles.videoIframe}
                                src={`${selectedVideoUrl}?autoplay=1`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                              />
                            ) : (
                              <div className={styles.videoImage}>
                                <span style={{ backgroundImage: `url(${VIDEO_LIST[0].image})` }} />
                                <i className={styles.videoImageIcon}>
                                  <IconPlayButton />
                                </i>
                              </div>
                            )}
                            <div className={styles.videoTitle}>
                              Understanding the Exchange Layout
                            </div>
                            <span className={styles.videoDescription}>
                              {'From simple back and lay odds, to liquidity, overrounds & matched volume. We run through the basics of the Matchbook Betting Exchange.'}
                            </span>
                          </div>
                          <div
                            role="button"
                            className={styles.video}
                            onClick={onVideoSelect(VIDEO_LIST[2].url)}
                          >
                            {selectedVideoUrl === VIDEO_LIST[2].url && isMobile ? (
                              <iframe
                                width="100%"
                                height="100%"
                                className={styles.videoIframe}
                                src={`${selectedVideoUrl}?autoplay=1`}
                                title="YouTube video player"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                allowFullScreen
                              />
                            ) : (
                              <div className={styles.videoImage}>
                                <span style={{ backgroundImage: `url(${VIDEO_LIST[2].image})` }} />
                                <i className={styles.videoImageIcon}>
                                  <IconPlayButton />
                                </i>
                              </div>
                            )}
                            <div className={styles.videoTitle}>
                              Exchange vs Sportsbook
                            </div>
                            <span className={styles.videoDescription}>
                              {"They're similar, but there are some key differences. Most of which benefit you. Let's look at the differences between Matchbook Betting Exchange and a traditional sportsbook."}
                            </span>
                          </div>
                        </span>
                      </div>
                      {isMobile ? (
                        <div className={styles.mobileInfo}>
                          <span>
                            {"Have you ever wanted to try out exchange betting, but didn't know where to start?"}
                          </span>
                          <br />
                          <br />
                          <span>
                            {'Here we have a selection of helpful educational videos to get you started, or you can take a quick guided tour below.'}
                          </span>
                        </div>
                      ) : (
                        <div className={styles.divider} />
                      )}
                    </div>
                    <div className={styles.actions}>
                      {firstTimeVisitor ? (
                        <Fragment>
                          <Button
                            sizeType="medium"
                            buttonType="plain"
                            className={styles.button}
                            onClick={handleTour}
                          >Take a tour</Button>
                          <Button
                            sizeType="medium"
                            buttonType="primary"
                            onClick={handleSignUp}
                          >Join Matchbook</Button>
                        </Fragment>
                      ) : (
                        <Fragment>
                          <Button
                            sizeType="medium"
                            buttonType="plain"
                            className={styles.button}
                            onClick={handleClose}
                          >Close</Button>
                          <Button
                            sizeType="medium"
                            buttonType="primary"
                            onClick={handleTour}
                          >Take a tour</Button>
                        </Fragment>
                      )}
                    </div>
                  </Fragment>
                )}
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
    </NativeModal>
  );
};

CustomerOnboarding.propTypes = {
  widthSize: PropTypes.number.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  firstTimeVisitor: PropTypes.bool.isRequired,
  startTourMode: PropTypes.func.isRequired,
  setHighlightedSidebarButton: PropTypes.func.isRequired,
  registrationRequest: PropTypes.func.isRequired,
  toggleCustomerOnboardingPopup: PropTypes.func.isRequired,
};

export default CustomerOnboarding;
